import '../pages.css';
import './TestsManagement.css';
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FlaskConical, Pencil } from 'lucide-react';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';
import TestDesign from './TestDesign';
import TestExecution from './TestExecution';
import TestCaseDetailModal from '../../components/TestCaseDetail/TestCaseDetailModal';
import CreateTestCaseModal from '../../components/TestCaseDetail/CreateTestCaseModal';
import useTestCaseDetail from '../../hooks/useTestCaseDetail';

function buildFolderBody(project, name, parentFolderId) {
  return { project, name, parentFolderId };
}

function findFolderById(node, id) {
  if (!node) return null;
  if (node.id === id) return node;
  for (const child of node.children ?? []) {
    const found = findFolderById(child, id);
    if (found) return found;
  }
  return null;
}

function TestsManagement() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'design');
  const [designProjects, setDesignProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(() => searchParams.get('project') || null);
  const [cases, setCases] = useState([]);
  const [totalApiCases, setTotalApiCases] = useState(0);
  const [loadedApiPages, setLoadedApiPages] = useState(0);
  const [currentFetchParams, setCurrentFetchParams] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [folderTree, setFolderTree] = useState(null); // root FolderTreeDto or null
  const [selectedFolder, setSelectedFolder] = useState(null); // { id, name } | null = root
  const [recursive, setRecursive] = useState(() => searchParams.get('recursive') === 'true');
  const [detailOpen, setDetailOpen] = useState(false);
  const { detail, loading: detailLoading, error: detailError, fetchById: fetchDetailById, clear: clearDetail } = useTestCaseDetail();
  const [createModalOpen, setCreateModalOpen] = useState(false);

  function updateParams(updates) {
    const next = new URLSearchParams(window.location.search);
    Object.entries(updates).forEach(([k, v]) => {
      if (v == null) next.delete(k);
      else next.set(k, v);
    });
    const qs = next.toString();
    navigate(qs ? `?${qs}` : window.location.pathname, { replace: true });
  }

  function openDetail(id, key) {
    setDetailOpen(true);
    if (Number.isInteger(id) && id > 0) {
      fetchDetailById(id);
      updateParams({ testId: id });
    }
  }

  function closeDetail() {
    setDetailOpen(false);
    clearDetail();
    updateParams({ testId: null });
  }

  const DISPLAY_FIELDS = 'key,id,summary,status,priority,parentId,unit,reporter,created,automationStatus,testPyramid,type,mobileFramework,mobileOs,folderId';

  function normalizeQSCase(tc) {
    return {
      ...tc,
      automationStatus: tc.automationStatus ?? null,
      testPyramid: tc.testPyramid ?? null,
      parentId: tc.parentId ?? null,
      mobile_framework: tc.mobileFramework == null
        ? null
        : Array.isArray(tc.mobileFramework)
          ? tc.mobileFramework.join(', ')
          : tc.mobileFramework,
      mobile_os: tc.mobileOs == null
        ? []
        : Array.isArray(tc.mobileOs)
          ? tc.mobileOs
          : [tc.mobileOs],
    };
  }

  async function fetchCasesFromQS(folderId, isRecursive, page = 1) {
    const res = await api.get(`${QUALITY_SERVICE_API_URL}/hub/test-cases`, {
      params: { folderId, getRecurssive: isRecursive, fields: DISPLAY_FIELDS, pageSize: 100, page },
    });
    return res.data; // { data, total, page, pageSize }
  }

  async function loadProjectData(project, isRecursive, targetFolderId = null) {
    setCases([]);
    setLoading(true);
    setError(null);
    try {
      const treeRes = await api.get(`${QUALITY_SERVICE_API_URL}/hub/test-cases/folders`, { params: { project } });
      const tree = treeRes.data;
      setFolderTree(tree);
      if (tree) {
        let folderId = tree.id;
        let resolvedFolder = null;
        if (targetFolderId != null) {
          const found = findFolderById(tree, targetFolderId);
          if (found) {
            resolvedFolder = { id: found.id, name: found.name };
            folderId = found.id;
          }
        }
        setSelectedFolder(resolvedFolder);
        const { data, total } = await fetchCasesFromQS(folderId, isRecursive);
        setCases(data.map(normalizeQSCase));
        setTotalApiCases(total);
        setLoadedApiPages(1);
        setCurrentFetchParams({ folderId, isRecursive });
      }
    } catch {
      setError('Failed to load test cases.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    api.get(`${QUALITY_SERVICE_API_URL}/hub/test-cases/projects`)
      .then(res => setDesignProjects(res.data))
      .catch(() => {});
    const testId = searchParams.get('testId');
    const parsedTestId = Number(testId);
    if (Number.isInteger(parsedTestId) && parsedTestId > 0) {
      setDetailOpen(true);
      fetchDetailById(parsedTestId);
    }
    const project = searchParams.get('project');
    if (project && searchParams.get('tab') !== 'execution') {
      const urlFolderId = Number(searchParams.get('folder')) || null;
      loadProjectData(project, recursive, urlFolderId);
    }
    const liveParams = new URLSearchParams(window.location.search);
    if (!liveParams.get('project') && window.location.search) {
      navigate(window.location.pathname, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function selectProject(project) {
    if (selectedProject === project) {
      if (selectedFolder !== null) selectFolder(null);
      return;
    }
    setSelectedProject(project);
    updateParams({ project, tab: activeTab, folder: null });
    setSelectedFolder(null);
    setFolderTree(null);
    loadProjectData(project, recursive, null);
  }

  async function selectFolder(folder) {
    if (selectedFolder?.id === folder?.id) return;
    const folderId = folder ? folder.id : folderTree?.id;
    if (!folderId) return;
    setSelectedFolder(folder);
    updateParams({ folder: folder?.id ?? null });
    setCases([]);
    setLoading(true);
    setError(null);
    try {
      const { data, total } = await fetchCasesFromQS(folderId, recursive);
      setCases(data.map(normalizeQSCase));
      setTotalApiCases(total);
      setLoadedApiPages(1);
      setCurrentFetchParams({ folderId, isRecursive: recursive });
    } catch {
      setError('Failed to load test cases.');
    } finally {
      setLoading(false);
    }
  }

  async function toggleRecursive() {
    const newRecursive = !recursive;
    setRecursive(newRecursive);
    updateParams({ recursive: newRecursive || null });
    if (!folderTree) return;
    const folderId = selectedFolder ? selectedFolder.id : folderTree.id;
    setCases([]);
    setLoading(true);
    setError(null);
    try {
      const { data, total } = await fetchCasesFromQS(folderId, newRecursive);
      setCases(data.map(normalizeQSCase));
      setTotalApiCases(total);
      setLoadedApiPages(1);
      setCurrentFetchParams({ folderId, isRecursive: newRecursive });
    } catch {
      setError('Failed to load test cases.');
    } finally {
      setLoading(false);
    }
  }

  function selectProjectExecution(project) {
    if (selectedProject === project) return;
    setSelectedProject(project);
    updateParams({ project, tab: 'execution', cycle: null, run: null });
  }

  function switchTab(nextTab) {
    if (activeTab === nextTab) return;
    setActiveTab(nextTab);
    if (nextTab === 'design') {
      setSelectedFolder(null);
      setRecursive(false);
      if (selectedProject && !folderTree) {
        loadProjectData(selectedProject, false, null);
      }
    }
    const current = new URLSearchParams(window.location.search);
    const project = current.get('project');
    const next = new URLSearchParams();
    if (project) {
      next.set('project', project);
      next.set('tab', nextTab);
    }
    const qs = next.toString();
    navigate(qs ? `?${qs}` : window.location.pathname, { replace: true });
  }

  async function deleteFolder(folder) {
    try {
      await api.delete(`${QUALITY_SERVICE_API_URL}/test-case/folder/${folder.id}`);
      const wasSelected = selectedFolder?.id === folder.id;
      const nextFolderId = wasSelected ? null : selectedFolder?.id ?? null;
      if (wasSelected) updateParams({ folder: null });
      await loadProjectData(selectedProject, recursive, nextFolderId);
    } catch {
      setError(`Failed to delete folder "${folder.name}".`);
    }
  }

  async function renameFolder(folder, name) {
    await api.patch(`${QUALITY_SERVICE_API_URL}/hub/test-cases/folders/${folder.id}`, { name });
    if (selectedFolder?.id === folder.id) setSelectedFolder(prev => ({ ...prev, name }));
    const treeRes = await api.get(`${QUALITY_SERVICE_API_URL}/hub/test-cases/folders`, { params: { project: selectedProject } });
    setFolderTree(treeRes.data);
  }

  async function createFolder(parentNode, name) {
    const parentFolderId = parentNode?.id ?? folderTree?.id;
    await api.post(`${QUALITY_SERVICE_API_URL}/hub/test-cases/folders`, buildFolderBody(selectedProject, name, parentFolderId));
    // Fetch tree directly so errors propagate to the + popover instead of being
    // swallowed by loadProjectData's internal catch.
    const treeRes = await api.get(`${QUALITY_SERVICE_API_URL}/hub/test-cases/folders`, { params: { project: selectedProject } });
    setFolderTree(treeRes.data);
    const folderId = selectedFolder ? selectedFolder.id : treeRes.data?.id;
    if (folderId) {
      const fetchedCases = await fetchCasesFromQS(folderId, recursive);
      setCases(fetchedCases.data.map(normalizeQSCase));
    }
  }

  async function refreshCases() {
    if (!currentFetchParams) return;
    const { data, total } = await fetchCasesFromQS(currentFetchParams.folderId, currentFetchParams.isRecursive);
    setCases(data.map(normalizeQSCase));
    setTotalApiCases(total);
    setLoadedApiPages(1);
  }

  async function loadMoreCases() {
    if (!currentFetchParams || loadingMore) return;
    const nextPage = loadedApiPages + 1;
    setLoadingMore(true);
    try {
      const { data, total } = await fetchCasesFromQS(currentFetchParams.folderId, currentFetchParams.isRecursive, nextPage);
      setCases(prev => [...prev, ...data.map(normalizeQSCase)]);
      setTotalApiCases(total);
      setLoadedApiPages(nextPage);
    } catch {
      setError('Failed to load more test cases.');
    } finally {
      setLoadingMore(false);
    }
  }

  async function saveTestCase(key, dto, newFolder) {
    if (newFolder?.name?.trim()) {
      const project = detail?.project ?? selectedProject;
      // Create the folder first — dto.folderPath will resolve to it in the subsequent PUT.
      const parentFolderId = newFolder.parentFolderId ?? folderTree?.id;
      const folderRes = await api.post(`${QUALITY_SERVICE_API_URL}/hub/test-cases/folders`, buildFolderBody(project, newFolder.name.trim(), parentFolderId));
      const createdFolder = folderRes.data;
      await api.put(`${QUALITY_SERVICE_API_URL}/hub/test-cases/jira/${key}`, dto);
      const treeRes = await api.get(`${QUALITY_SERVICE_API_URL}/hub/test-cases/folders`, { params: { project } });
      setFolderTree(treeRes.data);
      if (createdFolder?.id) {
        setSelectedFolder({ id: createdFolder.id, name: createdFolder.name });
        const fetchedCases = await fetchCasesFromQS(createdFolder.id, recursive);
        setCases(fetchedCases.data.map(normalizeQSCase));
      }
    } else {
      await api.put(`${QUALITY_SERVICE_API_URL}/hub/test-cases/jira/${key}`, dto);
      await refreshCases();
    }
    setTimeout(() => fetchDetailById(detail?.id), 2000);
  }

  async function deleteTestCase(key) {
    await api.delete(`${QUALITY_SERVICE_API_URL}/hub/test-cases/jira/${key}`);
    closeDetail();
    await refreshCases();
  }

  // Bulk-update the editable test-case fields on many cases at once. Writes go to
  // Jira only; Postgres is synced back asynchronously via webhook, so we optimistically
  // apply the chosen values to the succeeded keys instead of refetching immediately.
  async function bulkUpdateCases({ project, keys, updates }) {
    const res = await api.put(`${QUALITY_SERVICE_API_URL}/hub/test-cases/jira/bulk`, {
      project,
      keys,
      updates,
    });
    const { updated = [] } = res.data || {};
    if (updated.length) {
      const updatedSet = new Set(updated);
      // `labels` is an ADD/REPLACE/REMOVE instruction, not a row value — skip it in the
      // optimistic merge; the webhook refetch below reflects the resolved label set.
      const optimistic = { ...updates };
      delete optimistic.labels;
      setCases(prev => prev.map(tc => (updatedSet.has(tc.key) ? { ...tc, ...optimistic } : tc)));
      // The DB catches up from Jira via webhook asynchronously — refetch after a short
      // delay so the list reflects the persisted values, not just the optimistic ones.
      // Matches the 2s delay used by the single-case edit path (saveTestCase).
      setTimeout(() => { refreshCases(); }, 2000);
    }
    return res.data;
  }

  async function createTestCase(dto) {
    const res = await api.post(`${QUALITY_SERVICE_API_URL}/hub/test-cases/jira`, dto);
    const rawId = res.data?.id;
    const id = Number(rawId);
    if (!Number.isFinite(id) || id <= 0) {
      throw new Error('Unexpected response: missing test case id.');
    }
    if (dto.project === selectedProject) {
      await refreshCases();
    }
    setDetailOpen(true);
    setTimeout(() => fetchDetailById(id), 2000);
  }

  return (
    <div className="application-container tm-page">
      <TestCaseDetailModal
        isOpen={detailOpen}
        onClose={closeDetail}
        loading={detailLoading}
        error={detailError}
        detail={detail}
        onSave={saveTestCase}
        onDelete={deleteTestCase}
        folderTree={folderTree}
      />
      <CreateTestCaseModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={createTestCase}
        projects={designProjects}
        defaultProject={selectedProject}
        folderTree={folderTree}
      />
      <div className="tm-header">
        <h1>Tests Management</h1>
<div className="tm-project-selector-wrap">
          <select
            className="tm-project-selector"
            value={selectedProject || ''}
            onChange={e => {
              const p = e.target.value || null;
              if (!p) {
                setSelectedProject(null);
                setFolderTree(null);
                setSelectedFolder(null);
                setCases([]);
                navigate(window.location.pathname, { replace: true });
              } else {
                selectProject(p);
              }
            }}
          >
            <option value="">Select Project...</option>
            {designProjects.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="tm-tab-nav">
        <button
          className={`tm-tab-btn ${activeTab === 'design' ? 'active' : ''}`}
          onClick={() => switchTab('design')}
        >
          <Pencil size={14} />
          Test Design
        </button>
        <button
          className={`tm-tab-btn ${activeTab === 'execution' ? 'active' : ''}`}
          onClick={() => switchTab('execution')}
        >
          <FlaskConical size={14} />
          Test Execution
        </button>
      </div>
      {activeTab === 'design' && (
        <TestDesign
          projects={designProjects}
          selectedProject={selectedProject}
          cases={cases}
          totalApiCases={totalApiCases}
          loading={loading}
          loadingMore={loadingMore}
          error={error}
          folderTree={folderTree}
          selectedFolder={selectedFolder}
          recursive={recursive}
          onSelectProject={selectProject}
          onSelectFolder={selectFolder}
          onToggleRecursive={toggleRecursive}
          onCaseClick={openDetail}
          onDeleteFolder={deleteFolder}
          onRenameFolder={renameFolder}
          onCreateFolder={createFolder}
          onAddTest={() => setCreateModalOpen(true)}
          onLoadMore={loadMoreCases}
          onBulkUpdate={bulkUpdateCases}
        />
      )}

      {activeTab === 'execution' && (
        <TestExecution
          projects={designProjects}
          selectedProject={selectedProject}
          onSelectProject={selectProjectExecution}
          onCaseClick={openDetail}
        />
      )}
    </div>
  );
}

export default TestsManagement;
